/*!
 * Pahc get price list by customer data action.
 * Wraps the custom Retail Proxy operation so it can be
 * used both declaratively (as a Commerce data action)
 * and imperatively from views/components.
 */
import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import { pahcGetPriceListByCustomerAsync } from './DataActionExtension.g';
import { IPahcPriceListHeaderEntity } from './DataServiceEntities.g';

/**
 * Input for fetching a price list for a specific invoice account.
 */
export class PriceListByCustomerInput implements IActionInput {
    public readonly invoiceAccount: string;

    public constructor(invoiceAccount: string) {
        this.invoiceAccount = invoiceAccount;
    }

    public getCacheKey = () => `PahcPriceList-${this.invoiceAccount || 'anonymous'}`;

    public getCacheObjectType = () => 'PahcPriceList';

    public dataCacheType = (): CacheType => 'none';
}

/**
 * Generic input factory – allows wiring into pipelines/page definitions.
 * It attempts to infer the current customer's account number from the
 * request context, but views should normally construct the input directly.
 */
const createInput = (input: ICreateActionContext): PriceListByCustomerInput => {
    const getInvoiceAccountFromObject = (source: unknown): string | undefined => {
        if (!source || typeof source !== 'object') {
            return undefined;
        }
        const queue: unknown[] = [source];
        const visited = new Set<unknown>();
        const invoiceKeys = new Set(['invoiceaccount', 'invoiceaccountnumber']);

        while (queue.length > 0) {
            const current = queue.shift();
            if (!current || typeof current !== 'object' || visited.has(current)) {
                continue;
            }
            visited.add(current);

            const record = current as Record<string, unknown>;
            for (const [key, value] of Object.entries(record)) {
                const normalizedKey = key.toLowerCase();
                if (invoiceKeys.has(normalizedKey) && value !== undefined && value !== null) {
                    const normalizedValue = String(value).trim();
                    if (normalizedValue) {
                        return normalizedValue;
                    }
                }

                if (value && typeof value === 'object') {
                    queue.push(value);
                }
            }
        }

        return undefined;
    };

    const user = (input.requestContext && (input.requestContext.user as unknown)) as
        | {
              [key: string]: unknown;
              invoiceAccount?: string;
              invoiceAccountNumber?: string;
              InvoiceAccount?: string;
              InvoiceAccountNumber?: string;
          }
        | undefined;

    // eslint-disable-next-line no-console
    console.log('[pahc-get-price-list] createInput user snapshot', {
        hasUser: Boolean(user),
        userKeys: user && typeof user === 'object' ? Object.keys(user) : [],
        hasCustomer: Boolean((user as { customer?: unknown } | undefined)?.customer),
        customerKeys:
            (user as { customer?: unknown } | undefined)?.customer && typeof (user as { customer?: unknown }).customer === 'object'
                ? Object.keys((user as { customer?: Record<string, unknown> }).customer ?? {})
                : []
    });

    const requestContextAny = (input.requestContext as unknown) as Record<string, unknown> | undefined;
    const inputAny = input as { actionContext?: { requestCache?: unknown } };
    const resolverSource = {
        user,
        customer: requestContextAny?.customer,
        requestUser: requestContextAny?.user,
        requestCache: requestContextAny?.requestCache ?? inputAny.actionContext?.requestCache,
        requestContext: requestContextAny
    };

    // eslint-disable-next-line no-console
    console.log('[pahc-get-price-list] createInput requestContext snapshot', {
        requestContextKeys: requestContextAny ? Object.keys(requestContextAny) : [],
        hasRequestCustomer: Boolean(requestContextAny?.customer),
        hasRequestCache: Boolean(requestContextAny?.requestCache),
        requestCustomerKeys:
            requestContextAny?.customer && typeof requestContextAny.customer === 'object'
                ? Object.keys(requestContextAny.customer as Record<string, unknown>)
                : []
    });

    const invoiceAccountRaw = getInvoiceAccountFromObject(resolverSource);
    const invoiceAccount = invoiceAccountRaw ? String(invoiceAccountRaw).trim() : '';
    // eslint-disable-next-line no-console
    console.log('[pahc-get-price-list] createInput resolved invoice account', {
        invoiceAccountRaw,
        invoiceAccount,
        hasInvoiceAccount: Boolean(invoiceAccount)
    });

    return new PriceListByCustomerInput(invoiceAccount);
};

/**
 * Core implementation that calls the custom Retail Proxy operation.
 */
export async function getPriceListByCustomer(
    input: PriceListByCustomerInput,
    ctx: IActionContext
): Promise<IPahcPriceListHeaderEntity | null> {
    const debugPrefix = '[pahc-get-price-list]';
    // eslint-disable-next-line no-console
    console.log(`${debugPrefix} Action invoked`, {
        invoiceAccount: input.invoiceAccount
    });

    // Retail-proxy custom operation expects an `IContext` whose `callerContext`
    // contains a full `requestContext` (apiSettings, locale, etc.).
    const retailProxyContext: import('@msdyn365-commerce/retail-proxy').IContext = {
        callerContext: {
            requestContext: (ctx.requestContext as unknown) as import('@msdyn365-commerce/retail-proxy').ICallerContext['requestContext']
        }
    };

    try {
        // eslint-disable-next-line no-console
        console.time(`${debugPrefix} pahcGetPriceListByCustomerAsync`);
        const result = await pahcGetPriceListByCustomerAsync(retailProxyContext, input.invoiceAccount || '');
        // eslint-disable-next-line no-console
        console.timeEnd(`${debugPrefix} pahcGetPriceListByCustomerAsync`);
        // eslint-disable-next-line no-console
        console.log(`${debugPrefix} API call completed`, {
            hasResult: Boolean(result),
            linesCount: result?.PriceListLines?.length ?? 0
        });

        if (!result) {
            ctx.telemetry?.warning?.('[pahc-get-price-list] Empty response from price list API');
            // eslint-disable-next-line no-console
            console.warn(`${debugPrefix} Empty response from price list API`);
            return null;
        }

        return result;
    } catch (error) {
        const anyError = error as { statusCode?: number; message?: string };
        // eslint-disable-next-line no-console
        console.error(`${debugPrefix} API call failed`, {
            statusCode: anyError?.statusCode,
            message: anyError?.message
        });

        if (anyError?.statusCode === 404) {
            // Treat 404 from CRT as "no price list for this account" rather than a hard failure.
            ctx.telemetry?.warning?.(
                `[pahc-get-price-list] Price list not found for invoice account ${input.invoiceAccount}: ${anyError.message ?? ''}`
            );
            // eslint-disable-next-line no-console
            console.warn(`${debugPrefix} Price list not found; returning null`);
            return null;
        }

        ctx.telemetry?.exception?.(error as Error);
        throw error;
    }
}

/**
 * Data action registration so this can be referenced from module definitions.
 */
export const pahcGetPriceListByCustomerDataAction = createObservableDataAction({
    id: 'pahc-get-price-list-by-customer',
    action: getPriceListByCustomer as IAction<IPahcPriceListHeaderEntity | null>,
    input: createInput
});

export default pahcGetPriceListByCustomerDataAction;

/**
 * Convenience helper for imperative usage from views/components.
 */
export const fetchPriceListForCustomer = (ctx: IActionContext, invoiceAccount: string): Promise<IPahcPriceListHeaderEntity | null> =>
    getPriceListByCustomer(new PriceListByCustomerInput(invoiceAccount), ctx);
